import { defineAsyncComponent, type AsyncComponentLoader, h } from 'vue'
import AsyncErrorComponent from '@/components/shared/AsyncErrorComponent.vue'
import SkeletonComponent from '@/components/skeletons/SkeletonComponent.vue'
import { reportError } from '@/utils/error-reporter'
import type { RouteLocationNormalized } from 'vue-router'

export const isRetryableError = (error: Error): boolean => {
  const msg = error.message.toLowerCase()
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('error loading dynamic module') ||
    msg.includes('network error')
  )
}

export function handleRouterError(error: Error, to: RouteLocationNormalized) {
  if (isRetryableError(error)) {
    reportError(error, {
      severity: 'info',
      context: 'router:navigation-error',
      metadata: {
        section: 'router',
        values: {
          targetPath: to.fullPath,
          message: 'Navigation failed due to chunk load error'
        }
      }
    })
  }
}

export function createAsyncComponent(loader: AsyncComponentLoader) {
  return defineAsyncComponent({
    loader,
    loadingComponent: {
      render() {
        return h(SkeletonComponent, { amount: 1 })
      }
    },
    delay: 200,
    errorComponent: AsyncErrorComponent,
    timeout: 30000,
    onError(error, retry, fail, attempts) {
      if (isRetryableError(error) && attempts <= 3) {
        const waitWithBackoff = attempts === 1 ? 0 : 500 * (attempts - 1)

        setTimeout(() => {
          retry()
        }, waitWithBackoff)
      } else {
        // bugsnag still notifies this on failure - see bugsnag-onerror.ts
        fail()
      }
    }
  })
}
